import moment from "moment";  

export function parseUtcDateTime(value) {
    if (value == null || value === "") {
        return null;
    } 

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return moment.utc(value.getTime());
    }

    const fallback = moment.utc(value);
    return fallback.isValid() ? fallback : null;
}

/** UTC date-only for Last Comment Date grid display (matches Notice Date / Event Date). */
export function formatUtcDateOnly(value) {
    const parsed = parseUtcDateTime(value);
    if (!parsed) {
        return "-";
    }
    return parsed.format("DD MMM YYYY");
}

/**
 * Insert lastCommentCreatedDate after lastComment when missing from saved column settings.
 */
export function ensureLastCommentCreatedDateInColumnSettings(
  settings,
  defaultVisibility = {}
) {
  if (!Array.isArray(settings) || settings.length === 0) {
    return settings;
  }

  const normalized = settings.map((c) => ({
    ...c,
    column: c.column || c.name,
  }));

  if (normalized.some((c) => c.column === "lastCommentCreatedDate")) {
    return settings;
  }

  const lastCommentIdx = normalized.findIndex((c) => c.column === "lastComment");
  const isVisible = defaultVisibility.lastCommentCreatedDate ?? false;

  const newEntry = {
    column: "lastCommentCreatedDate",
    isVisible,
    enabledInDrawer: false,
  };

  if (lastCommentIdx < 0) {
    const maxOrder = normalized.reduce(
      (m, c) =>
        Math.max(m, Number.isFinite(Number(c.order)) ? Number(c.order) : 0),
      -1
    );
    return [...settings, { ...newEntry, order: maxOrder + 1 }];
  }

  const afterOrder = Number(normalized[lastCommentIdx].order);
  const baseOrder = Number.isFinite(afterOrder) ? afterOrder : lastCommentIdx;

  const updated = settings.map((c) => {
    const o = Number(c.order);
    if (Number.isFinite(o) && o > baseOrder) {
      return { ...c, order: o + 1 };
    }
    return c;
  });

  updated.splice(lastCommentIdx + 1, 0, { ...newEntry, order: baseOrder + 1 });
  return updated;
}

/**
 * Backfill one or more columns that are missing from a user's already-saved
 * column settings (e.g. columns added to the grid after that view/user
 * settings row was created). Missing columns are appended at the end, in the
 * order given in `columnKeys`, defaulting their visibility from
 * `defaultVisibility[column]` (falling back to `false`).
 *
 * Columns already present in `settings` are left untouched.
 */
export function ensureColumnsInColumnSettings(
  settings,
  columnKeys,
  defaultVisibility = {}
) {
  if (!Array.isArray(settings) || settings.length === 0) {
    return settings;
  }
  if (!Array.isArray(columnKeys) || columnKeys.length === 0) {
    return settings;
  }

  const normalized = settings.map((c) => ({
    ...c,
    column: c.column || c.name,
  }));
  const existing = new Set(normalized.map((c) => c.column));
  const missing = columnKeys.filter((key) => !existing.has(key));

  if (missing.length === 0) {
    return settings;
  }

  let maxOrder = normalized.reduce(
    (m, c) =>
      Math.max(m, Number.isFinite(Number(c.order)) ? Number(c.order) : 0),
    -1
  );

  const newEntries = missing.map((column) => {
    maxOrder += 1;
    return {
      column,
      isVisible: defaultVisibility[column] ?? false,
      enabledInDrawer: false,
      order: maxOrder,
    };
  });

  return [...settings, ...newEntries];
}